import { delay, getAIEmployerTiming } from '../../mock.config';
import employers from '../data/employers.json';
import boardEmployersData from '../data/boardEmployers.json';

/* ── In-memory state (mutations persist within the browser session) ────── */
let employersList = JSON.parse(JSON.stringify(employers));
let boardEmployers = JSON.parse(JSON.stringify(boardEmployersData));

/** Clone an object to avoid shared reference issues */
const clone = (obj) => JSON.parse(JSON.stringify(obj));

/** Reset in-memory state to initial JSON data (useful for testing) */
export function resetEmployerState() {
  employersList = JSON.parse(JSON.stringify(employers));
  boardEmployers = JSON.parse(JSON.stringify(boardEmployersData));
}

/**
 * Main handler for all employer-related HTTP requests.
 * @param {string} method - GET, POST, PUT, PATCH, DELETE
 * @param {string} url - Full request URL
 * @param {object} config - Axios config (with data, params)
 * @returns {Promise<object>} - Mock axios response data
 */
export async function handleEmployerRequest(method, url, config) {
  const timing = getAIEmployerTiming();
  await delay(timing.apiDelay);

  /* ── ADMIN / GLOBAL ENDPOINTS ────────────────────────────────────────── */

  // GET /admin/ai-employers — list all employers
  if (method === 'GET' && url.includes('/admin/ai-employers') && !url.match(/\/ai-employers\/[^/]+$/)) {
    const search = config?.params?.search?.toLowerCase();
    const filtered = search
      ? employersList.filter((e) => e.name.toLowerCase().includes(search) || e.role.toLowerCase().includes(search))
      : employersList;
    return { status: true, message: 'Employers fetched successfully', body: clone(filtered) };
  }

  // POST /admin/ai-employers — create employer
  if (method === 'POST' && url.includes('/admin/ai-employers') && !url.includes('/import')) {
    const body = JSON.parse(config?.data || '{}');
    const newEmployer = {
      _id: 'emp-custom-' + Date.now(),
      ...body,
      isActive: body.isActive ?? true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    employersList = [newEmployer, ...employersList];
    return { status: true, message: 'Employer created successfully', body: clone(newEmployer) };
  }

  // PUT /admin/ai-employers/:id — update employer
  if (method === 'PUT' && url.includes('/admin/ai-employers/')) {
    const id = url.split('/admin/ai-employers/')[1]?.split(/[/?]/)[0];
    const body = JSON.parse(config?.data || '{}');
    const idx = employersList.findIndex((e) => e._id === id);
    if (idx >= 0) {
      employersList[idx] = { ...employersList[idx], ...body, updatedAt: new Date().toISOString() };
      return { status: true, message: 'Employer updated', body: clone(employersList[idx]) };
    }
    return { status: false, message: 'Employer not found', body: null };
  }

  // DELETE /admin/ai-employers/:id
  if (method === 'DELETE' && url.includes('/admin/ai-employers/')) {
    const id = url.split('/admin/ai-employers/')[1]?.split(/[/?]/)[0];
    employersList = employersList.filter((e) => e._id !== id);
    return { status: true, message: 'Employer deleted' };
  }

  /* ── BOARD-LEVEL ENDPOINTS ───────────────────────────────────────────── */

  // GET /board/:boardId/ai-employers — list imported employers
  if (method === 'GET' && /\/board\/[^/]+\/ai-employers$/.test(url)) {
    return { status: true, message: 'Board employers fetched', body: { employers: clone(boardEmployers) } };
  }

  // GET /board/:boardId/ai-employers/available — employers NOT yet imported
  if (method === 'GET' && url.includes('/ai-employers/available')) {
    const importedIds = new Set(boardEmployers.map((b) => b.employerId));
    const available = employersList.filter((e) => !importedIds.has(e._id));
    return { status: true, message: 'Available employers fetched', body: { employers: clone(available) } };
  }

  // POST /board/:boardId/ai-employers/:employerId/import
  if (method === 'POST' && url.includes('/import')) {
    const parts = url.split('/ai-employers/');
    const employerId = parts[1]?.split('/')[0];
    const body = config?.data ? JSON.parse(config.data) : {};
    const employer = employersList.find((e) => e._id === employerId);
    if (!employer) return { status: false, message: 'Employer not found', body: null };

    const assignment = {
      assignmentId: 'asgn-' + employerId + '-' + Date.now(),
      employerId,
      boardId: 'board-mock-001',
      customInstructions: body.customInstructions || '',
      triggerMode: 'manual',
      canEdit: true,
      resources: employer.resourceCapabilities?.map((r) => r.type) || [],
      deniedResources: [],
      importedAt: new Date().toISOString(),
      employer: { _id: employer._id, name: employer.name, role: employer.role, position: employer.position },
    };
    boardEmployers = [...boardEmployers, assignment];
    return {
      status: true, message: 'Employer imported to board',
      body: { assignment: clone(assignment), employer: clone(employer) },
    };
  }

  // DELETE /board/:boardId/ai-employers/:employerId — remove from board
  if (method === 'DELETE' && /\/board\/[^/]+\/ai-employers\/[^/]+$/.test(url)) {
    const parts = url.split('/ai-employers/');
    const employerId = parts[1]?.split(/[/?]/)[0];
    boardEmployers = boardEmployers.filter((b) => b.employerId !== employerId && b.assignmentId !== employerId);
    return { status: true, message: 'Employer removed from board' };
  }

  /* ── ASSIGNMENT SETTINGS ─────────────────────────────────────────────── */

  // GET /board/:boardId/ai-employers/:employerId/assignment
  if (method === 'GET' && url.includes('/assignment')) {
    const parts = url.split('/ai-employers/');
    const id = parts[1]?.split('/')[0];
    const assignment = boardEmployers.find((b) => b.employerId === id || b.assignmentId === id);
    if (assignment) return { status: true, body: clone(assignment) };
    return { status: false, message: 'Assignment not found', body: null };
  }

  // PATCH /board/:boardId/ai-employers/:employerId/assignment
  if (method === 'PATCH' && url.includes('/assignment')) {
    const parts = url.split('/ai-employers/');
    const id = parts[1]?.split('/')[0];
    const body = JSON.parse(config?.data || '{}');
    const idx = boardEmployers.findIndex((b) => b.employerId === id || b.assignmentId === id);
    if (idx >= 0) {
      boardEmployers[idx] = { ...boardEmployers[idx], ...body };
      return { status: true, message: 'Assignment updated', body: clone(boardEmployers[idx]) };
    }
    return { status: false, message: 'Assignment not found', body: null };
  }

  // PATCH /board/:boardId/ai-employers/:employerId/trigger-mode
  if (method === 'PATCH' && url.includes('/trigger-mode')) {
    const parts = url.split('/ai-employers/');
    const id = parts[1]?.split('/')[0];
    const body = JSON.parse(config?.data || '{}');
    const idx = boardEmployers.findIndex((b) => b.employerId === id || b.assignmentId === id);
    if (idx >= 0) {
      boardEmployers[idx].triggerMode = body.triggerMode || 'manual';
      return { status: true, message: 'Trigger mode updated', body: clone(boardEmployers[idx]) };
    }
    return { status: false, message: 'Assignment not found' };
  }

  // PATCH /board/:boardId/ai-employers/:employerId/access-tokens
  if (method === 'PATCH' && url.includes('/access-tokens')) {
    return { status: true, message: 'Access tokens updated', body: {} };
  }

  /* ── TASK-LEVEL ENDPOINTS ────────────────────────────────────────────── */

  // GET /board/:boardId/tasks/:taskId/ai-employers — list task employers
  if (method === 'GET' && /\/tasks\/[^/]+\/ai-employers$/.test(url)) {
    const mainTaskEmps = boardEmployers.slice(0, 3).map((b) => ({
      memberId: b.assignmentId,
      employerId: b.employerId,
      employer: b.employer,
      duty: getDefaultDuty(b.employerId),
    }));

    const subTaskEmps = [
      {
        taskMapId: 'subtask-mock-001',
        taskMapText: 'API Documentation',
        employers: [
          {
            memberId: 'member-sub-sarah-001',
            employerId: 'emp-sarah-001',
            employer: { _id: 'emp-sarah-001', name: 'Sarah Chen', role: 'Senior Full-Stack Developer' },
            duty: 'Write OpenAPI spec for all endpoints',
          },
        ],
      },
    ];

    return {
      status: true, message: 'Task employers fetched',
      body: { mainTask: { employers: mainTaskEmps }, subTasks: subTaskEmps },
    };
  }

  // POST /board/:boardId/tasks/:taskId/ai-employers/:employerId — assign
  if (method === 'POST' && /\/tasks\/[^/]+\/ai-employers\/[^/]+$/.test(url)) {
    const parts = url.split('/ai-employers/');
    const employerId = parts[1]?.split(/[/?]/)[0];
    const body = JSON.parse(config?.data || '{}');
    const employer = employersList.find((e) => e._id === employerId);
    if (!employer) return { status: false, message: 'Employer not found' };
    return {
      status: true, message: 'Employer assigned to task',
      body: {
        memberId: 'member-' + employerId + '-task-' + Date.now(),
        employerId,
        employer: { _id: employer._id, name: employer.name, role: employer.role },
        duty: body.duty || getDefaultDuty(employerId),
      },
    };
  }

  // DELETE /board/:boardId/tasks/:taskId/ai-employers/:employerId — remove
  if (method === 'DELETE' && /\/tasks\/[^/]+\/ai-employers\/[^/]+/.test(url)) {
    return { status: true, message: 'Employer removed from task' };
  }

  return { status: false, message: 'Unknown employer endpoint: ' + method + ' ' + url };
}

function getDefaultDuty(employerId) {
  const duties = {
    'emp-sarah-001': 'Implement user authentication API with JWT',
    'emp-marcus-002': 'Design dashboard analytics page',
    'emp-alex-003': 'Write integration tests for auth API',
    'emp-diana-004': 'Set up staging environment with Docker Compose',
    'emp-james-005': 'Security audit of new auth API',
    'emp-emma-006': 'Create Q3 product roadmap',
  };
  return duties[employerId] || 'Assigned task execution';
}

export function getEmployersForTask(_boardId, _taskId) {
  return clone(boardEmployers).map((b) => ({
    memberId: b.assignmentId,
    employerId: b.employerId,
    employer: b.employer,
    duty: getDefaultDuty(b.employerId),
  }));
}
